import * as React from 'react';

import styled, { css } from 'styled-components';

import { DropdownContentType } from '@webapp/types/ui/dropdown-content-type';
import { motion } from 'framer-motion';
import { easeTransition } from '@components/framer';

export interface DropdownListProps {
  className?: string;
  children?: any;
  listOpen?: boolean;
  height?: number;
  noMaxHeight?: boolean;
  maxHeight?: number;
  noScroll?: boolean;
  customContent?: boolean;
  onMouseOver?: () => void;
  customPadding?: string;
  content?: DropdownContentType;
  isDark?: boolean;
  isDarker?: boolean;
  hasChanges?: boolean;
}

export const DropdownList: React.FC<DropdownListProps> = (props) => {
  const item = {
    visible: {
      y: 0,
      opacity: 1,
      height: '100%',
      transition: {
        ...easeTransition,
        duration: 0.25,
      },
    },
    hidden: {
      y: -100,
      height: 0,
      opacity: 0,
    },
  };

  return (
    <div
      className={props.className}
      onMouseOver={() => (props.onMouseOver ? props.onMouseOver() : null)}
    >
      <motion.div
        className="dropdown-list-container"
        initial="hidden"
        animate={props.listOpen ? 'visible' : 'hidden'}
        variants={item}
      >
        {props.children}
      </motion.div>
    </div>
  );
};

export default styled(DropdownList)`
  width: 100%;
  pointer-events: none;
  z-index: 999;
  position: absolute;
  top: 100%;
  right: 0;
  left: 0;
  transition: height
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  ${(props) =>
    props.customContent && props.listOpen
      ? css`
          height: 100%;
          pointer-events: initial;
        `
      : null}

  .inner-comp {
    border-bottom: 3px solid ${({ theme }) => theme.colors.differentBackground};
  }

  .dropdown-list-container {
    margin-top: ${(props) => (!props.customContent ? '3px' : '0')};
    filter: drop-shadow(
      0px 0px 20px ${({ theme }) => theme.colors.differentBackground}
    );

    ${(props) =>
      props.isDark || props.isDarker
        ? css`
            filter: drop-shadow(
              0px 0px 20px ${({ theme }) => theme.colors.dropdownBG}
            );
          `
        : null}

    .dropdown-list-content {
      padding: 10px;
      overflow-y: auto;
      background-color: ${({ theme }) => theme.colors.differentBackground};
      border-radius: 0 0 4px 4px;
      overflow-x: hidden;
      transition: height
          ${({ theme }) =>
            `${theme.transitions.duration} ${theme.transitions.type}`},
        margin-top
          ${({ theme }) =>
            `${theme.transitions.duration} ${theme.transitions.type}`};

      &.dark {
        background-color: ${({ theme }) => theme.colors.primary};
      }

      &.darker {
        background-color: ${({ theme }) => theme.colors.darkDropdown};
      }

      &.pill-shape {
        border-radius: 0 0 18px 18px;
        padding: 20px 16px;
        @media ${({ theme }) => theme.device.tablet} {
          border-radius: 0 0 24px 24px;
        }
      }

      ${(props) =>
        !props.noMaxHeight
          ? css`
              max-height: 250px;
            `
          : null};
      ${(props) =>
        props.maxHeight
          ? css`
              max-height: ${props.maxHeight}px;
            `
          : null};

      ${(props) =>
        props.noScroll
          ? css`
              padding: 15px 10px;
            `
          : null};

      @media ${({ theme }) => theme.device.tablet} {
        padding: 20px;

        ${(props) =>
          props.noScroll
            ? css`
                padding: 10px;
              `
            : null};
      }

      ${(props) =>
        props.customPadding
          ? css`
              padding: ${props.customPadding};

              @media ${({ theme }) => theme.device.tablet} {
                padding: ${props.customPadding};
              }
            `
          : null}
    }

    ${(props) =>
      props.customContent
        ? css`
            margin-top: ${props.listOpen ? '30px' : '0'};

            @media ${({ theme }) => theme.device.tablet} {
              margin-top: 0;
            }
          `
        : null}
  }

  ${(props) =>
    props.listOpen && !props.customContent
      ? css`
          pointer-events: initial;
          top: 51px;

          @media ${({ theme }) => theme.device.tablet} {
            top: 60px;
          }

          .container {
            .dropdown-list-content {
              overflow-y: auto;
              overflow-x: hidden;

              ::-webkit-scrollbar {
                width: 15px;
              }

              ::-webkit-scrollbar-track {
                background: transparent;
              }

              ::-webkit-scrollbar-thumb {
                background-color: ${({ theme }) =>
                  theme.colors.inputBackground};
                border-radius: 8px;
                border: 4px solid ${({ theme }) => theme.colors.box};
              }
            }
          }
        `
      : null}

  ${(props) =>
    props.listOpen && !props.customContent && props.isDark
      ? css`
          .container {
            .dropdown-list-content {
              ::-webkit-scrollbar-thumb {
                background-color: ${({ theme }) => theme.colors.blackActive};
                border: 4px solid ${({ theme }) => theme.colors.primary};
              }
            }
          }
        `
      : null}

  ${(props) =>
    props.listOpen && props.content === ''
      ? css`
          &:hover {
            span {
              opacity: 0.5;
            }
          }
        `
      : ''}
`;
